<template>
  <el-dialog
    v-model="visible"
    :title="t('version_update_title')"
    :width="dialogWidth"
    top="15vh"
    :lock-scroll="false"
    :modal="true"
    :close-on-click-modal="false"
    :show-close="false"
    :closable="false"
    class="version-update-dialog"
    :class="{
      'is-mobile-landscape': isMobileLandscape,
    }"
    @closed="handleClosed"
  >
    <div class="version-content">
      <div class="version-icon">🎉</div>
      <div class="version-text">
        {{ t('version_update_detected') }} <span class="version-number">{{ newVersion }}</span>
      </div>
      <div class="version-tip">{{ t('version_update_tip') }}</div>
    </div>
    <template #footer>
      <el-button type="primary" size="large" class="confirm-btn" @click="handleRefresh">
        {{ t('version_update_refresh') }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
/**
 * 版本更新提示弹窗组件
 *
 * 功能:
 * - 显示版本更新提示
 * - 用户只能点击"立即刷新"按钮
 * - 不可关闭,必须刷新页面
 * - 响应式设计,适配 PC 端和手机端
 * - 样式与 AdDialog 保持一致
 */

import { useI18n } from 'vue-i18n'
import { useDeviceDetect } from '@/composables/useDeviceDetect'

// 设备检测
const { isMobilePortrait, isMobileLandscape, isIPadMode } = useDeviceDetect()

// 多语言
const { t } = useI18n()

// 控制弹窗显示/隐藏
const visible = ref(false)

// 新版本号
const newVersion = ref('')

// 根据设备类型动态设置弹窗宽度
const dialogWidth = computed(() => {
  if (isMobilePortrait.value) {
    return '90%' // 手机竖屏
  }
  if (isMobileLandscape.value) {
    return '70%' // 手机横屏
  }
  if (isIPadMode.value) {
    return '60%' // iPad
  }
  return '600px' // PC端
})

// 定义 emit 事件
const emit = defineEmits<{
  refresh: []
}>()

/**
 * 显示弹窗
 */
const show = (version: string) => {
  newVersion.value = version
  visible.value = true
}

/**
 * 隐藏弹窗
 */
const hide = () => {
  visible.value = false
}

/**
 * 处理立即刷新
 */
const handleRefresh = () => {
  visible.value = false
  emit('refresh')
}

/**
 * 处理对话框关闭
 */
const handleClosed = () => {
  newVersion.value = ''
}

// 暴露方法供外部调用
defineExpose({
  show,
  hide,
})
</script>

<style scoped lang="scss">
// 版本更新弹窗特殊样式
:deep(.version-update-dialog) {
  .el-dialog {
    border-radius: 20px;
    background: linear-gradient(90deg, #261a0a 0%, #453011 48.56%, #261a0a 99.04%);
    border: 2px solid #fdc06d;
    box-shadow: 0px 8px 24px 0px rgba(0, 0, 0, 0.4);
  }

  .el-dialog__header {
    background-color: transparent;
    border-bottom: 1px solid #fdc06d;

    .el-dialog__title {
      color: #fff;
      font-size: 32px;
      font-weight: 600;

      // 手机竖屏: 缩小标题
      @media (max-width: 767px) and (min-height: 501px) {
        font-size: 28px;
      }
    }

    .el-dialog__headerbtn {
      .el-dialog__close {
        color: #fff;

        &:hover {
          color: #fdc06d;
        }
      }
    }
  }

  .el-dialog__body {
    min-height: 180px;
    padding: 40px 50px;
    background: transparent;

    // 手机竖屏: 减少 padding
    @media (max-width: 767px) and (min-height: 501px) {
      padding: 30px;
      min-height: 150px;
    }

    // 手机横屏: 减少 padding
    &.is-mobile-landscape {
      padding: 30px;
    }
  }

  .el-dialog__footer {
    padding: 20px 50px 50px;
    text-align: center;
    background: transparent;

    // 手机竖屏: 减少 padding
    @media (max-width: 767px) and (min-height: 501px) {
      padding: 15px 30px 30px;
    }
  }
}

.version-content {
  text-align: center;
  color: #e1e1e1;
}

.version-icon {
  font-size: 64px;
  margin-bottom: 20px;

  // 手机竖屏: 缩小图标
  @media (max-width: 767px) and (min-height: 501px) {
    font-size: 48px;
    margin-bottom: 15px;
  }
}

.version-text {
  font-size: 28px;
  line-height: 1.6;
  margin-bottom: 10px;

  // 手机竖屏: 缩小字体
  @media (max-width: 767px) and (min-height: 501px) {
    font-size: 24px;
  }
}

.version-number {
  color: #fdc06d;
  font-weight: 600;
  font-size: 32px;

  // 手机竖屏: 缩小字体
  @media (max-width: 767px) and (min-height: 501px) {
    font-size: 26px;
  }
}

.version-tip {
  font-size: 22px;
  color: #b8b8b8;
  line-height: 1.6;

  // 手机竖屏: 缩小字体
  @media (max-width: 767px) and (min-height: 501px) {
    font-size: 18px;
  }
}

.confirm-btn {
  width: 100%;
  height: 60px;
  font-size: 24px;
  background: linear-gradient(180deg, #ffb12c 0%, #ffc445 100%);
  color: #463011;
  border: none;
  font-weight: 600;
  border-radius: 8px;

  // 手机竖屏: 缩小按钮
  @media (max-width: 767px) and (min-height: 501px) {
    min-width: 80%;
    height: 50px;
    font-size: 20px;
  }

  &:hover {
    background: linear-gradient(180deg, #ffc445 0%, #ffd666 100%);
    box-shadow: 0px 6px 16px 0px rgba(255, 177, 44, 0.5);
  }

  &:active {
    background: linear-gradient(180deg, #ffb12c 0%, #ffa500 100%);
    transform: scale(0.98);
  }
}
</style>
